import type {
  EquipmentAssessmentConfig,
  AssessmentConfigFormData,
  ValidationResult
} from '../types';

export const MAX_CHAR_LENGTH = 200;

export const parseCommaSeparatedValues = (input: string): string[] => {
  if (!input?.trim()) return [];

  const values = input
    .split(',')
    .map(v => v.trim())
    .filter(v => v.length > 0);

  return [...new Set(values)];
};

export const arrayToCommaSeparated = (values: string[]): string => {
  return values.join(', ');
};

export const parseAssessmentConfig = (
  blobText: string | null | undefined
): EquipmentAssessmentConfig => {
  const defaultConfig: EquipmentAssessmentConfig = {
    condition: { values: [], required: false },
    operability: { values: [], required: false }
  };

  if (!blobText) return defaultConfig;

  try {
    const parsed = JSON.parse(blobText);
    return {
      condition: {
        values: Array.isArray(parsed?.condition?.values)
          ? parsed.condition.values
          : [],
        required: Boolean(parsed?.condition?.required)
      },
      operability: {
        values: Array.isArray(parsed?.operability?.values)
          ? parsed.operability.values
          : [],
        required: Boolean(parsed?.operability?.required)
      },
      version: parsed?.version
    };
  } catch {
    return defaultConfig;
  }
};

export const stringifyAssessmentConfig = (
  config: EquipmentAssessmentConfig
): string => {
  return JSON.stringify(config);
};

export type ValidationErrorKey = 'atLeastOneValue' | 'maxLength';

export const validateAssessmentConfig = (
  formData: AssessmentConfigFormData
): ValidationResult => {
  const errors: ValidationResult['errors'] = {};

  const conditionValues = parseCommaSeparatedValues(formData.conditionValues);
  const operabilityValues = parseCommaSeparatedValues(
    formData.operabilityValues
  );

  if (conditionValues.length === 0) {
    errors.conditionValues = 'atLeastOneValue';
  } else if (formData.conditionValues.length > MAX_CHAR_LENGTH) {
    errors.conditionValues = 'maxLength';
  }

  if (operabilityValues.length === 0) {
    errors.operabilityValues = 'atLeastOneValue';
  } else if (formData.operabilityValues.length > MAX_CHAR_LENGTH) {
    errors.operabilityValues = 'maxLength';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const formDataToConfig = (
  formData: AssessmentConfigFormData
): EquipmentAssessmentConfig => {
  return {
    condition: {
      values: parseCommaSeparatedValues(formData.conditionValues),
      required: formData.conditionRequired
    },
    operability: {
      values: parseCommaSeparatedValues(formData.operabilityValues),
      required: formData.operabilityRequired
    },
    version: 1
  };
};

export const configToFormData = (
  config: EquipmentAssessmentConfig
): AssessmentConfigFormData => {
  return {
    conditionValues: arrayToCommaSeparated(config.condition.values),
    conditionRequired: config.condition.required,
    operabilityValues: arrayToCommaSeparated(config.operability.values),
    operabilityRequired: config.operability.required
  };
};
